import React, { useState, useEffect } from 'react';
import { useUnifiedAuth } from './UnifiedAuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { 
  AlertTriangle, FileText, CheckCircle, XCircle, Loader2,
  ChevronLeft, Calendar, User, Shield, Clock,
  Plus, Eye, MessageSquare, Send, Scale
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIPOS_NOTIFICACAO = {
  advertencia_verbal: { label: 'Advertência Verbal', cor: 'bg-amber-500' },
  advertencia_escrita: { label: 'Advertência Escrita', cor: 'bg-orange-500' },
  suspensao: { label: 'Suspensão', cor: 'bg-red-500' },
  aviso_demissao: { label: 'Aviso de Demissão', cor: 'bg-red-700' }
};

const TIPOS_INFRACAO = {
  ausencia: 'Ausência sem justificativa',
  atraso: 'Entrada atrasada',
  sem_intervalo: 'Não realizar intervalo obrigatório',
  hora_extra_nao_autorizada: 'Horas extras sem autorização',
  outro: 'Outro (especificar)'
};

const STATUS_CONFIG = {
  pendente: { label: 'Aguardando Recebimento', cor: 'bg-amber-500', icon: Clock },
  recebida: { label: 'Recebida', cor: 'bg-blue-500', icon: CheckCircle },
  contestada: { label: 'Contestada', cor: 'bg-purple-500', icon: MessageSquare },
  respondida: { label: 'Respondida', cor: 'bg-emerald-500', icon: CheckCircle },
  finalizada: { label: 'Finalizada', cor: 'bg-slate-500', icon: CheckCircle },
  cancelada: { label: 'Cancelada', cor: 'bg-slate-400', icon: XCircle }
};

const UnifiedNotificacoesDisciplinares = () => {
  const navigate = useNavigate();
  const { jornyxToken, jornyxUser } = useUnifiedAuth();
  const [loading, setLoading] = useState(true);
  const [dados, setDados] = useState({ recebidas: [], criadas: [], total_pendentes: 0 });
  const [activeTab, setActiveTab] = useState('recebidas');
  const [notificacaoSelecionada, setNotificacaoSelecionada] = useState(null);
  const [showCriarDialog, setShowCriarDialog] = useState(false);
  const [showContestarDialog, setShowContestarDialog] = useState(false);
  const [showResponderDialog, setShowResponderDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [subordinados, setSubordinados] = useState([]);
  
  const [novaNotificacao, setNovaNotificacao] = useState({
    empregado_id: '',
    tipo: 'advertencia_escrita',
    tipo_infracao: 'atraso',
    descricao_infracao: '',
    data_ocorrencia: new Date().toISOString().split('T')[0],
    dias_suspensao: 1,
    data_inicio_suspensao: '',
    data_fim_suspensao: '',
    testemunha_1: '',
    testemunha_2: ''
  });
  
  const [razoesContestacao, setRazoesContestacao] = useState('');
  const [respostaContestacao, setRespostaContestacao] = useState('');
  const [manterPunicao, setManterPunicao] = useState(true);

  const isGestor = jornyxUser?.is_gestor;

  useEffect(() => {
    if (jornyxToken) {
      carregarDados();
      if (isGestor) {
        carregarSubordinados();
      }
    }
  }, [jornyxToken, isGestor]);

  const carregarDados = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/app/notificacoes-disciplinares`, {
        headers: { 'Authorization': `Bearer ${jornyxToken}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setDados(data);
      }
    } catch (err) {
      console.error('Erro ao carregar notificações:', err);
      toast.error('Erro ao carregar notificações');
    } finally {
      setLoading(false);
    }
  };

  const carregarSubordinados = async () => {
    try {
      const res = await fetch(`${API_URL}/api/empregados?empresa_id=${jornyxUser?.empresa_id}`, {
        headers: { 'Authorization': `Bearer ${jornyxToken}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        const subs = (data.empregados || data).filter(e => 
          e.lider_id === jornyxUser?.empregado_id && e.ativo !== false
        );
        setSubordinados(subs);
      }
    } catch (err) {
      console.error('Erro ao carregar subordinados:', err);
    }
  };

  const criarNotificacao = async () => {
    if (!novaNotificacao.empregado_id) {
      toast.error('Selecione o empregado');
      return;
    }
    if (novaNotificacao.tipo_infracao === 'outro' && !novaNotificacao.descricao_infracao) {
      toast.error('Descreva a infração');
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/app/notificacoes-disciplinares`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${jornyxToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(novaNotificacao)
      });
      
      if (res.ok) {
        toast.success('Notificação criada e enviada ao empregado!');
        setShowCriarDialog(false);
        resetNovaNotificacao();
        carregarDados();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao criar notificação');
      }
    } catch (err) {
      toast.error('Erro ao criar notificação');
    } finally {
      setSubmitting(false);
    }
  };

  const resetNovaNotificacao = () => {
    setNovaNotificacao({
      empregado_id: '',
      tipo: 'advertencia_escrita',
      tipo_infracao: 'atraso',
      descricao_infracao: '',
      data_ocorrencia: new Date().toISOString().split('T')[0],
      dias_suspensao: 1,
      data_inicio_suspensao: '',
      data_fim_suspensao: '',
      testemunha_1: '',
      testemunha_2: ''
    });
  };

  const confirmarRecebimento = async () => {
    if (!notificacaoSelecionada) return;
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/app/notificacoes-disciplinares/${notificacaoSelecionada.id}/receber`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${jornyxToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (res.ok) {
        toast.success('Recebimento confirmado!');
        setNotificacaoSelecionada(null);
        carregarDados();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao confirmar');
      }
    } catch (err) {
      toast.error('Erro ao confirmar recebimento');
    } finally {
      setSubmitting(false);
    }
  };

  const enviarContestacao = async () => {
    if (!razoesContestacao.trim()) {
      toast.error('Informe as razões da contestação');
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/app/notificacoes-disciplinares/${notificacaoSelecionada.id}/contestar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${jornyxToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ razoes: razoesContestacao })
      });
      
      if (res.ok) {
        toast.success('Contestação enviada!');
        setShowContestarDialog(false);
        setRazoesContestacao('');
        setNotificacaoSelecionada(null);
        carregarDados();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao contestar');
      }
    } catch (err) {
      toast.error('Erro ao enviar contestação');
    } finally {
      setSubmitting(false);
    }
  };

  const responderContestacao = async () => {
    if (!respostaContestacao.trim()) {
      toast.error('Informe a resposta');
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/app/notificacoes-disciplinares/${notificacaoSelecionada.id}/responder-contestacao`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${jornyxToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          resposta: respostaContestacao,
          manter_punicao: manterPunicao
        })
      });
      
      if (res.ok) {
        toast.success('Resposta enviada!');
        setShowResponderDialog(false);
        setRespostaContestacao('');
        setManterPunicao(true);
        setNotificacaoSelecionada(null);
        carregarDados();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao responder');
      }
    } catch (err) {
      toast.error('Erro ao responder contestação');
    } finally {
      setSubmitting(false);
    }
  };

  const formatarData = (data) => {
    if (!data) return '-';
    try {
      if (data.includes('T')) {
        const d = new Date(data);
        return d.toLocaleDateString('pt-BR');
      }
      const [ano, mes, dia] = data.split('-');
      return `${dia}/${mes}/${ano}`;
    } catch {
      return data;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  // Tela de detalhes da notificação
  if (notificacaoSelecionada && !showContestarDialog && !showResponderDialog) {
    const tipoConfig = TIPOS_NOTIFICACAO[notificacaoSelecionada.tipo] || TIPOS_NOTIFICACAO.advertencia_escrita;
    const statusConfig = STATUS_CONFIG[notificacaoSelecionada.status] || STATUS_CONFIG.pendente;
    const StatusIcon = statusConfig.icon;
    const isPendente = notificacaoSelecionada.status === 'pendente';
    const isRecebida = notificacaoSelecionada.status === 'recebida';
    const isContestada = notificacaoSelecionada.status === 'contestada';
    const isDestinatario = notificacaoSelecionada.empregado_id === jornyxUser?.empregado_id;
    const isCriador = notificacaoSelecionada.gestor_id === jornyxUser?.empregado_id;
    
    return (
      <div className="space-y-4 p-4 pb-24">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <Button 
            variant="ghost" 
            onClick={() => setNotificacaoSelecionada(null)}
            className="p-2"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h2 className="text-lg font-bold text-slate-800">Notificação Disciplinar</h2>
            <p className="text-sm text-slate-500">{tipoConfig.label}</p>
          </div>
          <Badge className={`${statusConfig.cor} text-white`}>
            <StatusIcon className="w-3 h-3 mr-1" />
            {statusConfig.label}
          </Badge>
        </div>
        
        {/* Texto Formal */}
        <Card className="border-l-4 border-l-red-500 bg-slate-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-4 text-slate-700">
              <Scale className="w-5 h-5" />
              <span className="font-semibold">Notificação Formal</span>
            </div>
            <div className="prose prose-sm max-w-none">
              <p className="whitespace-pre-line text-slate-800 leading-relaxed">
                {notificacaoSelecionada.texto_formal || 'Texto não disponível'}
              </p>
            </div>
          </CardContent>
        </Card>
        
        {/* Detalhes */}
        <Card>
          <CardContent className="pt-6 space-y-3">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-slate-500 block">Empregado</span>
                <span className="font-medium text-slate-800">{notificacaoSelecionada.empregado_nome}</span>
              </div>
              <div>
                <span className="text-slate-500 block">Data do Fato</span>
                <span className="font-medium text-slate-800">{formatarData(notificacaoSelecionada.data_ocorrencia)}</span>
              </div>
              <div>
                <span className="text-slate-500 block">Emitido por</span>
                <span className="font-medium text-slate-800">{notificacaoSelecionada.gestor_nome}</span>
              </div>
              <div>
                <span className="text-slate-500 block">Data da Emissão</span>
                <span className="font-medium text-slate-800">{formatarData(notificacaoSelecionada.created_at)}</span>
              </div>
            </div>
            
            {notificacaoSelecionada.dias_suspensao && notificacaoSelecionada.tipo === 'suspensao' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-4">
                <span className="text-red-700 font-semibold block">
                  Suspensão de {notificacaoSelecionada.dias_suspensao} dia(s)
                </span>
                {notificacaoSelecionada.data_inicio_suspensao && (
                  <span className="text-red-600 text-sm">
                    De {formatarData(notificacaoSelecionada.data_inicio_suspensao)} a {formatarData(notificacaoSelecionada.data_fim_suspensao)}
                  </span>
                )}
              </div>
            )}
            
            {(notificacaoSelecionada.testemunha_1 || notificacaoSelecionada.testemunha_2) && (
              <div className="mt-4 pt-4 border-t">
                <span className="text-slate-500 block mb-1">Testemunhas</span>
                <div className="flex flex-col gap-1">
                  {notificacaoSelecionada.testemunha_1 && (
                    <span className="text-slate-700 text-sm">• {notificacaoSelecionada.testemunha_1}</span>
                  )}
                  {notificacaoSelecionada.testemunha_2 && (
                    <span className="text-slate-700 text-sm">• {notificacaoSelecionada.testemunha_2}</span>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Contestações */}
        {notificacaoSelecionada.contestacoes && notificacaoSelecionada.contestacoes.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                Contestações e Respostas
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {notificacaoSelecionada.contestacoes.map((c, idx) => (
                <div 
                  key={c.id || idx} 
                  className={`p-3 rounded-lg ${
                    c.autor_tipo === 'empregado' 
                      ? 'bg-purple-50 border-l-4 border-purple-500' 
                      : 'bg-emerald-50 border-l-4 border-emerald-500'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-medium text-sm">
                      {c.autor_nome || (c.autor_tipo === 'empregado' ? 'Empregado' : 'Gestor')}
                    </span>
                    <span className="text-xs text-slate-400">
                      {formatarData(c.created_at)}
                    </span>
                  </div>
                  <p className="text-sm text-slate-700">
                    {c.razoes || c.resposta}
                  </p>
                  {c.manter_punicao === false && (
                    <Badge className="mt-2 bg-green-500 text-white text-xs">
                      Punição cancelada
                    </Badge>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        )}
        
        {/* Ações do empregado */}
        {isPendente && isDestinatario && (
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 mb-4 text-amber-700">
                <AlertTriangle className="w-5 h-5" />
                <span className="font-semibold">Confirmação Necessária</span>
              </div>
              <p className="text-amber-800 text-sm mb-4">
                Você precisa confirmar o recebimento desta notificação para continuar usando o aplicativo.
              </p>
              <Button 
                className="w-full bg-amber-600 hover:bg-amber-700"
                onClick={confirmarRecebimento}
                disabled={submitting}
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                Confirmar Recebimento
              </Button>
            </CardContent>
          </Card>
        )}
        
        {/* Opção de contestar após receber */}
        {(isRecebida || notificacaoSelecionada.status === 'respondida') && isDestinatario && (
          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 mb-4 text-purple-700">
                <MessageSquare className="w-5 h-5" />
                <span className="font-semibold">Deseja Contestar?</span>
              </div>
              <p className="text-purple-800 text-sm mb-4">
                Se você discorda da notificação, pode abrir uma contestação expondo suas razões.
              </p>
              <Button 
                variant="outline"
                className="w-full border-purple-400 text-purple-700 hover:bg-purple-100"
                onClick={() => setShowContestarDialog(true)}
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Abrir Contestação
              </Button>
            </CardContent>
          </Card>
        )}
        
        {/* Opção de responder contestação (gestor) */}
        {isContestada && isCriador && (
          <Card className="bg-emerald-50 border-emerald-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 mb-4 text-emerald-700">
                <MessageSquare className="w-5 h-5" />
                <span className="font-semibold">Contestação Recebida</span>
              </div>
              <p className="text-emerald-800 text-sm mb-4">
                O empregado contestou a notificação. Você precisa responder.
              </p>
              <Button 
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                onClick={() => setShowResponderDialog(true)}
              >
                <Send className="w-4 h-4 mr-2" />
                Responder Contestação
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // Tela principal com abas
  return (
    <div className="space-y-4 p-4 pb-24">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/unified')}
          className="p-2"
        >
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h2 className="text-xl font-bold text-slate-800">Notificações Disciplinares</h2>
          <p className="text-sm text-slate-500">
            {isGestor ? 'Gerencie notificações da sua equipe' : 'Suas notificações'}
          </p>
        </div>
        {isGestor && (
          <Button 
            onClick={() => setShowCriarDialog(true)}
            className="bg-red-600 hover:bg-red-700"
          >
            <Plus className="w-4 h-4 mr-1" />
            Nova
          </Button>
        )}
      </div>
      
      {/* Contador de pendentes */}
      {dados.total_pendentes > 0 && (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="py-3">
            <div className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="w-5 h-5" />
              <span className="font-medium">
                {dados.total_pendentes} notificação(ões) aguardando seu recebimento
              </span>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Tabs */}
      {isGestor && (
        <div className="flex gap-2 border-b border-slate-200 pb-2">
          <Button
            variant={activeTab === 'recebidas' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab('recebidas')}
          >
            Recebidas ({dados.recebidas?.length || 0})
          </Button>
          <Button
            variant={activeTab === 'criadas' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab('criadas')}
          >
            Criadas ({dados.criadas?.length || 0})
          </Button>
        </div>
      )}
      
      {/* Lista de notificações */}
      <div className="space-y-3">
        {(activeTab === 'recebidas' ? dados.recebidas : dados.criadas)?.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center">
              <Shield className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">
                {activeTab === 'recebidas' 
                  ? 'Nenhuma notificação recebida' 
                  : 'Nenhuma notificação criada'}
              </p>
            </CardContent>
          </Card>
        ) : (
          (activeTab === 'recebidas' ? dados.recebidas : dados.criadas)?.map((notif) => {
            const tipoConfig = TIPOS_NOTIFICACAO[notif.tipo] || TIPOS_NOTIFICACAO.advertencia_escrita;
            const statusConfig = STATUS_CONFIG[notif.status] || STATUS_CONFIG.pendente;
            const StatusIcon = statusConfig.icon;
            
            return (
              <Card 
                key={notif.id} 
                className={`cursor-pointer hover:shadow-md transition-all ${
                  notif.status === 'pendente' ? 'border-amber-300 bg-amber-50/50 ring-1 ring-amber-400' : 
                  notif.status === 'contestada' ? 'border-purple-300 bg-purple-50/50' : ''
                }`}
                onClick={() => setNotificacaoSelecionada(notif)}
              >
                <CardContent className="py-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${tipoConfig.cor}`}>
                        <AlertTriangle className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-800">{tipoConfig.label}</p>
                        <p className="text-sm text-slate-500">
                          {activeTab === 'recebidas' 
                            ? `De: ${notif.gestor_nome}` 
                            : `Para: ${notif.empregado_nome}`}
                        </p>
                        <p className="text-xs text-slate-400">{formatarData(notif.created_at)}</p>
                      </div>
                    </div>
                    <Badge className={`${statusConfig.cor} text-white`}>
                      <StatusIcon className="w-3 h-3 mr-1" />
                      {statusConfig.label}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
      
      {/* Dialog Criar Notificação */}
      <Dialog open={showCriarDialog} onOpenChange={setShowCriarDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nova Notificação Disciplinar</DialogTitle>
            <DialogDescription>
              Emitir notificação conforme Art. 482 CLT
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Empregado *</Label>
              <Select 
                value={novaNotificacao.empregado_id} 
                onValueChange={(v) => setNovaNotificacao({...novaNotificacao, empregado_id: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o empregado" />
                </SelectTrigger>
                <SelectContent>
                  {subordinados.map((sub) => (
                    <SelectItem key={sub.id} value={sub.id}>
                      {sub.nome} {sub.matricula ? `(${sub.matricula})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Tipo de Punição *</Label>
              <Select 
                value={novaNotificacao.tipo} 
                onValueChange={(v) => setNovaNotificacao({...novaNotificacao, tipo: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(TIPOS_NOTIFICACAO).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      {config.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Tipo de Infração *</Label>
              <Select 
                value={novaNotificacao.tipo_infracao} 
                onValueChange={(v) => setNovaNotificacao({...novaNotificacao, tipo_infracao: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(TIPOS_INFRACAO).map(([key, label]) => (
                    <SelectItem key={key} value={key}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {novaNotificacao.tipo_infracao === 'outro' && (
              <div>
                <Label>Descreva a Infração *</Label>
                <Textarea
                  value={novaNotificacao.descricao_infracao}
                  onChange={(e) => setNovaNotificacao({...novaNotificacao, descricao_infracao: e.target.value})}
                  placeholder="Descreva a infração cometida..."
                  rows={2}
                />
              </div>
            )}
            
            {novaNotificacao.tipo === 'suspensao' && (
              <>
                <div>
                  <Label>Dias de Suspensão *</Label>
                  <Input
                    type="number"
                    min="1"
                    max="30"
                    value={novaNotificacao.dias_suspensao}
                    onChange={(e) => setNovaNotificacao({
                      ...novaNotificacao, 
                      dias_suspensao: parseInt(e.target.value) || 1
                    })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Início da Suspensão</Label>
                    <Input
                      type="date"
                      value={novaNotificacao.data_inicio_suspensao}
                      onChange={(e) => setNovaNotificacao({...novaNotificacao, data_inicio_suspensao: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Fim da Suspensão</Label>
                    <Input
                      type="date"
                      value={novaNotificacao.data_fim_suspensao}
                      onChange={(e) => setNovaNotificacao({...novaNotificacao, data_fim_suspensao: e.target.value})}
                    />
                  </div>
                </div>
              </>
            )}
            
            <div>
              <Label>Data do Fato *</Label>
              <Input
                type="date"
                value={novaNotificacao.data_ocorrencia}
                onChange={(e) => setNovaNotificacao({...novaNotificacao, data_ocorrencia: e.target.value})}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Testemunha 1</Label>
                <Input
                  value={novaNotificacao.testemunha_1}
                  onChange={(e) => setNovaNotificacao({...novaNotificacao, testemunha_1: e.target.value})}
                  placeholder="Nome"
                />
              </div>
              <div>
                <Label>Testemunha 2</Label>
                <Input
                  value={novaNotificacao.testemunha_2}
                  onChange={(e) => setNovaNotificacao({...novaNotificacao, testemunha_2: e.target.value})}
                  placeholder="Nome"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCriarDialog(false)}>
              Cancelar
            </Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={criarNotificacao} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Emitir Notificação
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Dialog Contestar */}
      <Dialog open={showContestarDialog} onOpenChange={setShowContestarDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Abrir Contestação</DialogTitle>
            <DialogDescription>
              Exponha suas razões para contestar esta notificação
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-800">
                Sua contestação será enviada ao gestor responsável, que deverá respondê-la.
                Mesmo contestando, a notificação permanece registrada no seu prontuário.
              </p>
            </div>
            
            <div>
              <Label>Suas Razões *</Label>
              <Textarea
                value={razoesContestacao}
                onChange={(e) => setRazoesContestacao(e.target.value)}
                placeholder="Explique por que discorda da notificação..."
                rows={5}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowContestarDialog(false);
              setRazoesContestacao('');
            }}>
              Cancelar
            </Button>
            <Button className="bg-purple-600 hover:bg-purple-700" onClick={enviarContestacao} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Enviar Contestação
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Dialog Responder Contestação (Gestor) */}
      <Dialog open={showResponderDialog} onOpenChange={setShowResponderDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Responder Contestação</DialogTitle>
            <DialogDescription>
              Analise as razões do empregado e responda
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {notificacaoSelecionada?.contestacoes?.filter(c => c.autor_tipo === 'empregado').map((c, idx) => (
              <div key={idx} className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                <p className="font-medium text-purple-800 text-sm mb-1">Razões do empregado:</p>
                <p className="text-purple-700 text-sm">{c.razoes}</p>
              </div>
            ))}
            
            <div>
              <Label>Sua Resposta *</Label>
              <Textarea
                value={respostaContestacao}
                onChange={(e) => setRespostaContestacao(e.target.value)}
                placeholder="Responda à contestação do empregado..."
                rows={4}
              />
            </div>
            
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
              <input
                type="checkbox"
                id="manterPunicao"
                checked={manterPunicao}
                onChange={(e) => setManterPunicao(e.target.checked)}
                className="w-4 h-4"
              />
              <label htmlFor="manterPunicao" className="text-sm text-slate-700">
                Manter a punição aplicada
              </label>
            </div>
            
            {!manterPunicao && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-sm text-green-800">
                  Ao desmarcar, a notificação será cancelada e a punição removida.
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowResponderDialog(false);
              setRespostaContestacao('');
              setManterPunicao(true);
            }}>
              Cancelar
            </Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={responderContestacao} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Enviar Resposta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UnifiedNotificacoesDisciplinares;
